import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Laptop, Check } from 'lucide-react';
import { useThemeStore, type ThemeMode } from '@/store/themeStore';

interface ThemeToggleProps {
  className?: string;
  showDropdown?: boolean;
}

export function ThemeToggle({ className = '', showDropdown = false }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const CurrentIcon = resolvedTheme === 'dark' ? Moon : Sun;

  if (!showDropdown) {
    return (
      <button
        type="button"
        onClick={toggleTheme}
        className={`relative p-2 rounded-lg bg-cinema-850/80 hover:bg-cinema-800 text-cinema-400 hover:text-cinema-100 border border-cinema-700/60 shadow-sm transition-colors duration-150 enabled:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp ${className}`}
        title={`Current mode: ${resolvedTheme}. Click to switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode.`}
        aria-label={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
      >
        <CurrentIcon className="w-4 h-4 transition-transform duration-200" />
      </button>
    );
  }

  const options: { mode: ThemeMode; label: string; icon: typeof Sun }[] = [
    { mode: 'dark', label: 'Dark Cinema', icon: Moon },
    { mode: 'light', label: 'Light Cinema', icon: Sun },
    { mode: 'system', label: 'System Preference', icon: Laptop },
  ];

  return (
    <div ref={containerRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 p-2 rounded-lg bg-cinema-850/80 hover:bg-cinema-800 text-cinema-400 hover:text-cinema-100 border border-cinema-700/60 shadow-sm transition-colors duration-150 enabled:active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
        title="Toggle Theme"
        aria-expanded={isOpen}
        aria-label="Toggle Theme Menu"
      >
        <CurrentIcon className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-44 rounded-xl bg-cinema-900 border border-cinema-700 shadow-cinema-screen py-1 z-50 animate-fade-in contain-overscroll">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-cinema-400 hairline-b">
            Theme Appearance
          </div>
          {options.map(({ mode, label, icon: Icon }) => {
            const isSelected = theme === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setTheme(mode);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs font-medium transition-colors ${
                  isSelected
                    ? 'text-lamp bg-lamp/10'
                    : 'text-cinema-300 hover:text-cinema-100 hover:bg-cinema-800/60'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5" />
                  <span>{label}</span>
                </div>
                {isSelected && <Check className="w-3.5 h-3.5 text-lamp" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
